import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { useCommon } from "../../../context/CommonContext";
import masterService from "../../../services/masterService";

const { Title, Text } = Typography;
const { Search } = Input;

const AvatarList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} avatars`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
  });

  const getAvatars = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.search,
          ...params,
        };

        console.log("Fetching avatars with params:", requestParams);
        const response = await masterService.getAvatarList(requestParams);
        console.log("Avatars API Response:", response);

        if (response.data && response.data.status) {
          const avatarsData = response.data.data?.data || [];
          const paginationData = response.data.data?.pagination || {};

          console.log("Avatars data:", avatarsData);
          console.log("Pagination data:", paginationData);

          // Ensure qualitiesData is an array
          if (Array.isArray(avatarsData)) {
            setAvatars(avatarsData);
            setPagination((prev) => ({
              ...prev,
              total: paginationData.total || avatarsData.length,
              current: paginationData.currentPage || prev.current,
            }));
          } else {
            console.error("Avatars data is not an array:", avatarsData);
            setAvatars([]);
          }
        } else {
          notify.error({ message: "Failed to fetch avatars" });
          setAvatars([]);
        }
      } catch (error) {
        console.error("Error fetching avatars:", error);
        notify.error({ message: "Failed to fetch avatars" });
        setAvatars([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.search, pagination.pageSize, pagination.current]
  );

  // Load avatars data
  useEffect(() => {
    setPageTitle("Avatars");
    getAvatars();
  }, [getAvatars, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getAvatars({
      searchText: value,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getAvatars({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search,
    });
  };

  // Handle add avatar
  const handleAddAvatar = () => {
    navigate("/avatar/add/new");
  };

  // Handle edit avatar
  const handleEditAvatar = (avatarId) => {
    navigate(`/avatar/edit/${avatarId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Avatar",
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Order Number",
      dataIndex: "order_no",
      key: "order_no",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.order_no.localeCompare(b.order_no),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditAvatar(record.avatar_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Avatars ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search avatars..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAvatar}
              size="middle"
            >
              Add Avatar
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={avatars}
          rowKey="vq_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{
            emptyText: "No avatars found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default AvatarList;
